import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import {
  FedexRegistrationMfaOutDTO,
  FedexRegistrationStatusEnum,
  FedexRegistrationStatusOutDTO,
} from '@api/ringil-api.v2';
import { Field } from '@components/framework/formfield/Field.tsx';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgInfoCircle from '@icons/info.svg?react';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import dayjs from 'dayjs';
import FedexEulaGate from '@app/pages/customer/CompanyProfile/FedexEulaGate.tsx';
import FedexEtdImagesSection from '@app/pages/customer/CompanyProfile/FedexEtdImagesSection.tsx';
import FedexShipmentSettingsSection from '@app/pages/customer/CompanyProfile/FedexShipmentSettingsSection.tsx';
import FedexDisclaimer from '@components/RingilComponents/FedexDisclaimer.tsx';
import FedexLogo from '@components/RingilComponents/FedexLogo.tsx';
import FedexAccountsSection from '@app/pages/customer/CompanyProfile/FedexAccountsSection.tsx';

// mandated fallback wording (FedEx Integrator guidance) - hardcoded English, never localized
const FEDEX_TECH_SUPPORT_MESSAGE =
  'We are unable to process this request. Please try again later or call FedEx Customer Service and ask for technical support.';

// which registration call failed - Factor 1 (address) errors keep FedEx's own message so the
// customer can correct the address; Factor 2 failures show the mandated fallback
type RegistrationStep = 'ADDRESS' | 'PIN' | 'INVOICE';

interface ComponentProps {
  companyId: string;
}

/**
 * Customer self-service onboarding of the company's FedEx accounts (a company can
 * register several - e.g. one per country it ships from).
 * The customer enters only the account number + the address FedEx has on file;
 * FedEx either issues credentials immediately or requires MFA - a PIN sent to
 * the account's contact on file, or details of a recent FedEx invoice.
 */
const CompanyProfileFedex: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const {
    getFedexRegistrationStatus,
    startFedexRegistration,
    sendFedexRegistrationPin,
    confirmFedexRegistrationPin,
    confirmFedexRegistrationInvoice,
  } = useContext(ApiContext);

  const [status, setStatus] = useState<FedexRegistrationStatusOutDTO | null>(null);
  const [mfa, setMfa] = useState<FedexRegistrationMfaOutDTO | null>(null);
  const [error, setError] = useState<{ step: RegistrationStep; message: string; params?: string[] } | null>(null);

  // VALIDATION-type API errors are delivered via onValidationFailed and would be
  // silently swallowed without an explicit handler
  const errorCallbacksFor = (step: RegistrationStep) => ({
    onValidationFailed: (response: any) => {
      const firstMessage = Array.isArray(response) ? response[0] : null;
      setError(firstMessage
        ? { step, message: firstMessage.message, params: firstMessage.params }
        : { step, message: 'FedEx error' });
    },
  });

  // FedEx Integrator guidance: when the Factor-2 validation (PIN / invoice) fails, the customer
  // sees the mandated fallback wording and is directed to FedEx Customer Service; after the
  // phone bypass they resubmit Factor 1 (account + address) and get the credentials directly.
  const showTechSupportFallback =
    error != null && (error.step !== 'ADDRESS' || error.message === 'api.fedex.registration.error.tech_support');

  // registered company adds one more account through the same wizard
  const [addingAccount, setAddingAccount] = useState(false);

  // step 1 - account + registered address
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [residential, setResidential] = useState(false);
  const [customerName, setCustomerName] = useState('');

  // step 2 - MFA
  const [mfaMode, setMfaMode] = useState<'PIN' | 'INVOICE'>('PIN');
  const [pinOption, setPinOption] = useState<string | null>(null);
  const [pinSent, setPinSent] = useState(false);
  const [pin, setPin] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState<number | null>(null);
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [invoiceAmount, setInvoiceAmount] = useState<number | null>(null);
  const [invoiceCurrency, setInvoiceCurrency] = useState('CZK');

  const load = () => {
    getFedexRegistrationStatus(data => setStatus(data));
  };

  const resetWizard = () => {
    setMfa(null);
    setError(null);
    setAccountNumber('');
    setAccountName('');
    setStreet('');
    setCity('');
    setPostalCode('');
    setCountryCode('');
    setResidential(false);
    setCustomerName('');
    setPinSent(false);
    setPin('');
    setInvoiceNumber(null);
    setInvoiceDate(null);
    setInvoiceAmount(null);
  };

  const onRegistered = () => {
    resetWizard();
    setAddingAccount(false);
    load();
  };

  const startAddingAccount = () => {
    resetWizard();
    setAddingAccount(true);
  };

  useEffect(() => {
    load();
  }, [companyId]);

  const startFormValid = !!accountNumber.trim() && !!street.trim() && !!city.trim() && !!postalCode.trim() && !!countryCode && !!customerName.trim();

  const handleStart = () => {
    setError(null);
    startFedexRegistration(
      {
        accountNumber: accountNumber.trim(),
        name: accountName.trim() || undefined,
        street: street.trim(),
        city: city.trim(),
        postalCode: postalCode.trim(),
        countryCode,
        residential,
        customerName: customerName.trim(),
      },
      data => {
        if (data.fedexRegistrationStatus === FedexRegistrationStatusEnum.REGISTERED) {
          onRegistered();
        } else {
          setMfa(data.mfa);
          setPinOption(data.mfa?.pinOptions?.[0] ?? null);
          setMfaMode(data.mfa?.pinOptions?.length ? 'PIN' : 'INVOICE');
        }
      },
      null,
      errorCallbacksFor('ADDRESS'),
    );
  };

  const handleSendPin = () => {
    if (!mfa || !pinOption) return;
    setError(null);
    sendFedexRegistrationPin({ accountAuthToken: mfa.accountAuthToken, option: pinOption }, () => {
      setPinSent(true);
    }, null, errorCallbacksFor('PIN'));
  };

  const handleConfirmPin = () => {
    if (!mfa) return;
    setError(null);
    confirmFedexRegistrationPin(
      { accountAuthToken: mfa.accountAuthToken, accountNumber: accountNumber.trim(), name: accountName.trim() || undefined, secureCodePin: pin.trim() },
      () => onRegistered(),
      null,
      errorCallbacksFor('PIN'),
    );
  };

  const handleConfirmInvoice = () => {
    if (!mfa || invoiceNumber == null || invoiceDate == null || invoiceAmount == null) return;
    setError(null);
    confirmFedexRegistrationInvoice(
      {
        accountAuthToken: mfa.accountAuthToken,
        accountNumber: accountNumber.trim(),
        name: accountName.trim() || undefined,
        invoiceNumber,
        invoiceDate: dayjs(invoiceDate).format('YYYY-MM-DD'),
        invoiceAmount,
        invoiceCurrency,
      },
      () => onRegistered(),
      null,
      errorCallbacksFor('INVOICE'),
    );
  };

  if (status == null) return <></>;

  if (status.registered && !addingAccount) {
    return (
      <div className="flex flex-col gap-4 mt-4">
        <div className="flex flex-row items-center gap-4">
          <FedexLogo height={44} />
          <COHeadline variant={'h3'} title={tr('CompanyProfileFedex.title', 'FedEx account')} />
        </div>
        <COInlineMessage variant={'info'} icon={<SvgInfoCircle className="text-content-tertiary" />}>
          {tr(
            'CompanyProfileFedex.registeredAccounts',
            'Shipments and prices are handled through your connected FedEx accounts. In location settings you can limit which accounts a location may use; with more than one allowed account you pick it when sending the shipment to FedEx.',
          )}
        </COInlineMessage>
        <FedexAccountsSection accounts={status.accounts ?? []} onAddAccount={startAddingAccount} onChanged={load} />
        <FedexShipmentSettingsSection />
        <FedexEtdImagesSection />
        <FedexDisclaimer />
      </div>
    );
  }

  // FedEx mandates EULA acceptance before the Account Registration flow may start.
  // Wider than the rest of the tab on purpose: the agreement is rendered as the original
  // PDF pages, which are unreadable squeezed into the 700px form column.
  if (!status.eulaAccepted) {
    return (
      <div className="flex flex-col gap-4 mt-4 max-w-[1100px]">
        <FedexEulaGate onAccepted={() => load()} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-4 max-w-[700px]">
      <div className="flex flex-row items-center gap-4">
        <FedexLogo height={44} />
        <COHeadline variant={'h3'} title={tr('CompanyProfileFedex.title', 'FedEx account')} />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="text-sm font-medium text-red-800">
            {tr('CompanyProfileFedex.error', 'FedEx registration failed')}
          </div>
          <div className="text-sm text-red-700 mt-2">
            {showTechSupportFallback ? (
              <>
                <div className="font-medium">{FEDEX_TECH_SUPPORT_MESSAGE}</div>
                <div className="mt-2">
                  {tr(
                    'CompanyProfileFedex.techSupportHint',
                    'Once FedEx Customer Service confirms the bypass by phone, go back and submit your account number and address again — your credentials will then be issued directly.',
                  )}
                </div>
              </>
            ) : (
              <div className="font-medium">{tr(error.message, error.message)}</div>
            )}
            {error.params && error.params.length > 0 && (
              <div className="mt-2 text-xs">
                {error.params.map((param, index) => (
                  <div key={index} className="mt-1">{param}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!mfa && (
        <>
          <COInlineMessage variant={'info'} icon={<SvgInfoCircle className="text-content-tertiary" />}>
            {tr(
              'CompanyProfileFedex.intro',
              'Connect your FedEx account to receive your negotiated prices and book shipments. Enter your FedEx account number and the address registered with FedEx.',
            )}
          </COInlineMessage>

          <Field label={tr('CompanyProfileFedex.accountNumber', 'FedEx account number')} required>
            <InputText value={accountNumber} onChange={e => setAccountNumber(e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label={tr('CompanyProfileFedex.accountName', 'Account name (e.g. the site it ships from)')}>
            <InputText value={accountName} onChange={e => setAccountName(e.target.value)} maxLength={100} style={{ width: '100%' }} />
          </Field>
          <Field label={tr('CompanyProfileFedex.customerName', 'Company name (as registered with FedEx)')} required>
            <InputText value={customerName} onChange={e => setCustomerName(e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label={tr('CompanyProfileFedex.street', 'Street and number')} required>
            <InputText value={street} onChange={e => setStreet(e.target.value)} style={{ width: '100%' }} />
          </Field>
          <div className="flex flex-row gap-4">
            <Field label={tr('CompanyProfileFedex.city', 'City')} required>
              <InputText value={city} onChange={e => setCity(e.target.value)} />
            </Field>
            <Field label={tr('CompanyProfileFedex.postalCode', 'Postal code')} required>
              <InputText value={postalCode} onChange={e => setPostalCode(e.target.value)} />
            </Field>
            <Field label={tr('CompanyProfileFedex.country', 'Country')} required>
              <InputCountry value={countryCode} onChange={v => setCountryCode(v)} />
            </Field>
          </div>
          <InputCheckbox
            label={tr('CompanyProfileFedex.residential', 'Residential address')}
            value={residential}
            onChange={(_, value) => setResidential(!!value)}
          />
          <div className="flex flex-row gap-2">
            <RingilButton
              label={tr('CompanyProfileFedex.start', 'Connect FedEx account')}
              onClick={handleStart}
              disabled={!startFormValid}
            />
            {addingAccount && (
              <RingilButton
                mode={'text-only'}
                label={tr('CompanyProfileFedex.accounts.cancel', 'Cancel')}
                onClick={() => {
                  resetWizard();
                  setAddingAccount(false);
                }}
              />
            )}
          </div>
        </>
      )}

      {mfa && (
        <>
          <COInlineMessage variant={'info'} icon={<SvgInfoCircle className="text-content-tertiary" />}>
            {tr(
              'CompanyProfileFedex.mfaIntro',
              'FedEx requires a verification. Choose how to confirm that this account belongs to you.',
            )}
          </COInlineMessage>

          {/* Guidance: after a tech-support bypass the customer must be able to
              go back and resubmit Factor 1 (account number + address) */}
          <div>
            <RingilButton
              mode={'outlined-rectangle'}
              label={tr('CompanyProfileFedex.backToAccount', 'Back to account details')}
              onClick={() => {
                setMfa(null);
                setPinSent(false);
                setPin('');
                setError(null);
              }}
            />
          </div>

          {/* Factor-2 method choice - one explicit option per available method (FedEx guidance) */}
          <div className="flex flex-row flex-wrap gap-3">
            {(mfa.pinOptions ?? []).map(o => (
              <RingilButton
                key={o}
                mode={mfaMode === 'PIN' && pinOption === o ? 'filled' : 'outlined-rectangle'}
                label={tr(`CompanyProfileFedex.mfaMethod.${o}`, pinMethodLabel(o))}
                onClick={() => {
                  setMfaMode('PIN');
                  setPinOption(o);
                  setPinSent(false);
                }}
              />
            ))}
            {mfa.invoiceAllowed && (
              <RingilButton
                mode={mfaMode === 'INVOICE' ? 'filled' : 'outlined-rectangle'}
                label={tr('CompanyProfileFedex.modeInvoice', 'FedEx invoice details')}
                onClick={() => setMfaMode('INVOICE')}
              />
            )}
          </div>

          {mfaMode === 'PIN' && pinOption && (
            <div className="flex flex-col gap-3">
              <div className="text-sm text-content-secondary">
                {tr('CompanyProfileFedex.pinIntroContact', 'FedEx sends a 6-digit code to the contact registered on the account: {{contact}}', {
                  contact: (pinOption === 'EMAIL' ? mfa.maskedEmail : mfa.maskedPhone) ?? '',
                })}
              </div>
              <div className="flex flex-row gap-3 items-end">
                <RingilButton
                  label={pinSent
                    ? tr('CompanyProfileFedex.resendPin', 'Send again')
                    : tr('CompanyProfileFedex.sendPin', 'Send code')}
                  onClick={handleSendPin}
                  disabled={!pinOption}
                />
              </div>
              {pinSent && (
                <div className="flex flex-row gap-3 items-end">
                  <Field label={tr('CompanyProfileFedex.pin', 'Verification code')} required>
                    <InputText value={pin} onChange={e => setPin(e.target.value)} maxLength={6} style={{ width: '200px' }} />
                  </Field>
                  <RingilButton
                    label={tr('CompanyProfileFedex.confirm', 'Confirm')}
                    onClick={handleConfirmPin}
                    disabled={pin.trim().length !== 6}
                  />
                </div>
              )}
            </div>
          )}

          {mfaMode === 'INVOICE' && (
            <div className="flex flex-col gap-3">
              <div className="text-sm text-content-secondary">
                {tr('CompanyProfileFedex.invoiceIntro', 'Enter the details of a FedEx invoice issued to this account within the last 90 days.')}
              </div>
              <div className="flex flex-row gap-3">
                <Field label={tr('CompanyProfileFedex.invoiceNumber', 'Invoice number')} required>
                  <InputNumber value={invoiceNumber} onChange={e => setInvoiceNumber(e.value)} useGrouping={false} maxFractionDigits={0} />
                </Field>
                <Field label={tr('CompanyProfileFedex.invoiceDate', 'Invoice date')} required>
                  <Calendar
                    value={invoiceDate}
                    onChange={e => setInvoiceDate(e.value as Date)}
                    dateFormat="yy-mm-dd"
                    minDate={dayjs().subtract(90, 'day').toDate()}
                    maxDate={new Date()}
                  />
                </Field>
              </div>
              <div className="flex flex-row gap-3 items-end">
                <Field label={tr('CompanyProfileFedex.invoiceAmount', 'Invoice amount')} required>
                  <InputNumber value={invoiceAmount} onChange={e => setInvoiceAmount(e.value)} minFractionDigits={0} maxFractionDigits={2} />
                </Field>
                <Field label={tr('CompanyProfileFedex.invoiceCurrency', 'Currency')} required>
                  <InputText value={invoiceCurrency} onChange={e => setInvoiceCurrency(e.target.value)} maxLength={4} style={{ width: '100px' }} />
                </Field>
                <RingilButton
                  label={tr('CompanyProfileFedex.confirm', 'Confirm')}
                  onClick={handleConfirmInvoice}
                  disabled={invoiceNumber == null || invoiceDate == null || invoiceAmount == null || !invoiceCurrency.trim()}
                />
              </div>
            </div>
          )}
        </>
      )}

      <FedexDisclaimer />
    </div>
  );
};

const pinMethodLabel = (option: string): string => {
  switch (option) {
    case 'EMAIL': return 'Code via email';
    case 'SMS': return 'Code via SMS';
    case 'CALL': return 'Code via phone call';
    default: return option;
  }
};

export default CompanyProfileFedex;
